import React, { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import Drawer from "@/components/ui/drawer";
import Button from "@/components/ui/button";
import Input from "@/components/ui/input";
import { Close } from "@/components/icons";
import type { CompanyTrainingProfile } from "../types/company-training-profiles.types";
import styles from "./CompanyTrainingProfileFormDrawer.module.scss";

interface FormValues {
  companyName: string;
  contactPhoneNumber: string;
  trainingsTaken: string[];
  traineeEvaluations: string[];
}

const emptyForm: FormValues = {
  companyName: "",
  contactPhoneNumber: "",
  trainingsTaken: [],
  traineeEvaluations: [],
};

interface CompanyTrainingProfileFormDrawerProps {
  isOpen: boolean;
  onClose: () => void;
  mode: "add" | "edit";
  profile: CompanyTrainingProfile | null;
  onSubmit: (data: FormValues, id?: string) => void;
}

const CompanyTrainingProfileFormDrawer: React.FC<
  CompanyTrainingProfileFormDrawerProps
> = ({ isOpen, onClose, mode, profile, onSubmit }) => {
  const { t } = useTranslation();
  const [form, setForm] = useState<FormValues>(emptyForm);
  const [newTraining, setNewTraining] = useState("");
  const [newEvaluation, setNewEvaluation] = useState("");

  useEffect(() => {
    if (mode === "edit" && profile) {
      setForm({
        companyName: profile.companyName ?? "",
        contactPhoneNumber: profile.contactPhoneNumber ?? "",
        trainingsTaken: profile.trainingsTaken ?? [],
        traineeEvaluations: profile.traineeEvaluations ?? [],
      });
    } else {
      setForm(emptyForm);
    }
    setNewTraining("");
    setNewEvaluation("");
  }, [mode, profile, isOpen]);

  const handleFieldChange = (field: keyof FormValues, value: unknown) => {
    setForm((prev) => ({ ...prev, [field]: value }));
  };

  const addTraining = () => {
    const trimmed = newTraining.trim();
    if (!trimmed) return;
    if (form.trainingsTaken.includes(trimmed)) return;
    handleFieldChange("trainingsTaken", [...form.trainingsTaken, trimmed]);
    setNewTraining("");
  };

  const removeTraining = (index: number) => {
    handleFieldChange(
      "trainingsTaken",
      form.trainingsTaken.filter((_, i) => i !== index),
    );
  };

  const addEvaluation = () => {
    const trimmed = newEvaluation.trim();
    if (!trimmed) return;
    if (form.traineeEvaluations.includes(trimmed)) return;
    handleFieldChange("traineeEvaluations", [
      ...form.traineeEvaluations,
      trimmed,
    ]);
    setNewEvaluation("");
  };

  const removeEvaluation = (index: number) => {
    handleFieldChange(
      "traineeEvaluations",
      form.traineeEvaluations.filter((_, i) => i !== index),
    );
  };

  const isFormValid =
    form.companyName.trim().length >= 2 &&
    form.contactPhoneNumber.trim().length >= 7 &&
    form.trainingsTaken.length >= 1 &&
    form.traineeEvaluations.length >= 1;

  const handleSubmit = () => {
    if (!isFormValid) return;
    onSubmit(form, profile?.id);
  };

  return (
    <Drawer
      isOpen={isOpen}
      onClose={onClose}
      title={
        mode === "add"
          ? t("companyTrainingProfiles.drawer.addTitle")
          : t("companyTrainingProfiles.drawer.editTitle")
      }
      width="520px"
    >
      <div className={styles.form}>
        <Input
          label={t("companyTrainingProfiles.fields.companyName")}
          value={form.companyName}
          maxLength={200}
          onChange={(e) => handleFieldChange("companyName", e.target.value)}
          required
        />

        <Input
          label={t("companyTrainingProfiles.fields.contactPhoneNumber")}
          value={form.contactPhoneNumber}
          maxLength={10}
          inputMode="numeric"
          onChange={(e) =>
            handleFieldChange(
              "contactPhoneNumber",
              e.target.value.replace(/\D/g, ""),
            )
          }
          required
        />

        <div>
          <span className={`${styles.fieldLabel} ${styles.requiredLabel}`}>
            {t("companyTrainingProfiles.fields.trainingsTaken")}
          </span>
          <div className={styles.stringList}>
            <div className={styles.stringListHeader}>
              <div className={styles.stringListInput}>
                <Input
                  placeholder={t(
                    "companyTrainingProfiles.fields.trainingsTakenPlaceholder",
                  )}
                  value={newTraining}
                  onChange={(e) => setNewTraining(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter") {
                      e.preventDefault();
                      addTraining();
                    }
                  }}
                />
              </div>
              <div className={styles.stringListAddBtn}>
                <Button
                  variant="outlined"
                  color="primary"
                  size="small"
                  onClick={addTraining}
                  disabled={!newTraining.trim()}
                >
                  {t("companyTrainingProfiles.drawer.add")}
                </Button>
              </div>
            </div>
            {form.trainingsTaken.length > 0 && (
              <div className={styles.stringListChips}>
                {form.trainingsTaken.map((item, idx) => (
                  <span key={idx} className={styles.chip}>
                    {item}
                    <button
                      type="button"
                      className={styles.chipRemove}
                      onClick={() => removeTraining(idx)}
                      aria-label={t("common.remove")}
                    >
                      <Close width={12} height={12} />
                    </button>
                  </span>
                ))}
              </div>
            )}
          </div>
        </div>

        <div>
          <span className={`${styles.fieldLabel} ${styles.requiredLabel}`}>
            {t("companyTrainingProfiles.fields.traineeEvaluations")}
          </span>
          <div className={styles.stringList}>
            <div className={styles.stringListHeader}>
              <div className={styles.stringListInput}>
                <Input
                  placeholder={t(
                    "companyTrainingProfiles.fields.traineeEvaluationsPlaceholder",
                  )}
                  value={newEvaluation}
                  onChange={(e) => setNewEvaluation(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter") {
                      e.preventDefault();
                      addEvaluation();
                    }
                  }}
                />
              </div>
              <div className={styles.stringListAddBtn}>
                <Button
                  variant="outlined"
                  color="primary"
                  size="small"
                  onClick={addEvaluation}
                  disabled={!newEvaluation.trim()}
                >
                  {t("companyTrainingProfiles.drawer.add")}
                </Button>
              </div>
            </div>
            {form.traineeEvaluations.length > 0 && (
              <div className={styles.stringListChips}>
                {form.traineeEvaluations.map((item, idx) => (
                  <span key={idx} className={styles.chip}>
                    {item}
                    <button
                      type="button"
                      className={styles.chipRemove}
                      onClick={() => removeEvaluation(idx)}
                      aria-label={t("common.remove")}
                    >
                      <Close width={12} height={12} />
                    </button>
                  </span>
                ))}
              </div>
            )}
          </div>
        </div>

        <Button
          variant="contained"
          color="primary"
          onClick={handleSubmit}
          disabled={!isFormValid}
        >
          {mode === "add"
            ? t("companyTrainingProfiles.drawer.create")
            : t("companyTrainingProfiles.drawer.save")}
        </Button>
      </div>
    </Drawer>
  );
};

export default CompanyTrainingProfileFormDrawer;
